import React, { useEffect, useState } from "react";
import Close from "../../../assets/Close.svg";
import "./style.scss";
import ModalButton from "../components/modal-btn";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import { sessionModel } from "entities/session";
import { sircapApi } from "shared/api/sircap";
import { toast } from "react-toastify";
import { startAuthentication } from "@simplewebauthn/browser";
import TermsCheckbox from "pages/onboarding/components/terms-checkbox";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import OtpInput from "widgets/otp-input";

interface BankDetails {
  bank: string;
  logo: string;
  onBankChange: () => void;
}

interface CurrencyDetails {
  currency: string;
  icon: string;
  onCurrencyChange: () => void;
}

interface DepositFields extends BankDetails, CurrencyDetails {}

type PropsType = {
  title: string;
  text: string;
  btnText?: string;
  btnText2?: string;
  showSecondBtn: boolean;
  onClick: any;
  onClickSecondBtn?: () => void;
  onHide: any;
  closeIcon?: boolean;
  customModal?: string;
  customOutlet?: string;
  confirmDeal?: boolean;
  isLoading?: boolean;
  dealError?: boolean;
  depositFields?: DepositFields;
  depositAmount?: number;
  customBody?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: string;
};

const Modal: React.FC<PropsType> = ({
  title,
  text,
  btnText,
  btnText2,
  showSecondBtn,
  onClick,
  onClickSecondBtn,
  onHide,
  closeIcon = true,
  customModal,
  customOutlet,
  confirmDeal = false,
  isLoading = false,
  dealError = false,
  depositFields,
  depositAmount,
  onChange,
  icon,
  customBody,
}) => {
  const [pin, setPin] = useState("");
  const { t: y } = useTranslation("yup");
  const { t } = useTranslation("app");
  const { biometric, biometrics } = useStore(sessionModel.sessionStore);

  const biometricOptions = [
    { id: "1", term: t("bioConsent") },
    { id: "2", term: t("bioOwnership") },
    { id: "3", term: t("bioLiability") },
  ];

  const [isChecked, setIsChecked] = useState([false, false, false]);

  const handleChange = (index: number) => {
    const newChecked = [...isChecked];
    newChecked[index] = !newChecked[index];
    setIsChecked(newChecked);
  };

  const verifyBio = () => {
    return new Promise<boolean>(async (res) => {
      try {
        await NativeBiometric.verifyIdentity({});
        res(true);
      } catch (error) {
        res(false);
      }
    });
  };

  const bioOptions = async () => {
    try {
      const platform = Capacitor.getPlatform();
      if (!biometrics.includes(biometric)) {
        return;
      }

      if (platform === "ios" || platform === "android") {
        const verified = await verifyBio();
        if (!verified) {
          return;
        }

        NativeBiometric.getCredentials({
          server: window.location.hostname,
        })
          .then((value) => {
            if (value) {
              // const [_, pinCode] = value!.password.split("-");
              // setPin(pinCode);
              // onClick(pin);

              onClick();
            } else {
              toast.error("Invalid biometric authentication");
            }
          })
          .catch(() => {
            toast.error("Invalid biometric authentication");
          });
      } else {
        const response = await sircapApi.auth.biometricsOptions({});

        if (!response.data || !response.data.data) {
          toast.error("Invalid biometric authentication");
          return;
        }

        const options = response.data.data;
        const bioResp = await startAuthentication(options);

        sessionModel.sessionStore.setState({
          biometricOptions: bioResp,
        });

        onClick();
      }
    } catch (error) {
      // toast.error("Failed to remove biometrics")
    }
  };

  useEffect(() => {
    if (confirmDeal) bioOptions();
  }, []);

  const handleOnChange = (newValue: string) => {
    setPin(newValue);
  };

  const shouldDisableBtn =
    text !== "Biometrics"
      ? isLoading ||
        (confirmDeal && pin.length !== 4) ||
        (depositFields && !depositAmount)
      : isLoading ||
        !isChecked.every((v) => v) ||
        (confirmDeal && pin.length !== 4);

  return (
    <div onClick={onHide} className={`outlet ${customOutlet}`}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className={` ${customModal} modal accountAlreadyExistsModal`}
      >
        {closeIcon === true && (
          <div
            className={confirmDeal ? "closeDiv closeConfirmDeal" : "closeDiv"}
          >
            <img onClick={onHide} className="close" src={Close} alt="Close" />
          </div>
        )}

        {icon && <img style={{ marginBottom: "24px" }} src={icon} alt={icon} />}

        <p>{title}</p>
        {text !== "Biometrics" ? (
          <span>{text}</span>
        ) : (
          <div className="biometrics-options">
            {biometricOptions.map((item, index) => (
              <TermsCheckbox
                className={"bio-modal-checkbox"}
                key={item.id}
                term={item.term}
                htmlForId={item.id}
                isChecked={isChecked[index]}
                onChange={() => {
                  handleChange(index);
                }}
              />
            ))}
          </div>
        )}

        {customBody && customBody}

        {confirmDeal && (
          <div
            className="authCode-wrapper"
            style={{
              marginBlock: text !== "Biometrics" ? "24px 40px" : "20px 24px",
            }}
          >
            <OtpInput
              value={pin}
              onChange={handleOnChange}
              disabled={isLoading}
              error={false}
              isFilled={pin.length === 4}
              shouldAutoFocus={!biometric}
            />
            {/* <AuthCode
              autoFocus={!biometric}
              allowedCharacters="numeric"
              length={4}
              onChange={handleOnChange}
            /> */}

            {dealError && (
              <span className="error-verify">{y("incorrectPIN")}</span>
            )}
          </div>
        )}

        {depositFields && (
          <div className="deposit-fields-form">
            <div className="deposit-modal-field">
              <label htmlFor="depositField">{t("amount")}</label>
              <input
                id="depositField"
                type="number"
                placeholder={t("exactAmount")}
                autoFocus
                onChange={onChange}
                value={depositAmount}
              />
            </div>

            <div
              className="info-field"
              onClick={depositFields.onCurrencyChange}
            >
              <label id="bank">{t("currency")}</label>
              <div>
                <span>{depositFields.currency}</span>
                <img src={depositFields.icon} alt="icon" />
              </div>
            </div>

            <div className="info-field" onClick={depositFields.onBankChange}>
              <label id="bank">{t("bank")}</label>
              <div>
                <span>{depositFields.bank}</span>
                <img src={depositFields.logo} alt="logo" />
              </div>
            </div>
          </div>
        )}

        {btnText && (
          <ModalButton
            disabled={shouldDisableBtn}
            onClick={(e: any) => {
              e.stopPropagation();
              if (confirmDeal) {
                onClick(pin);
              } else {
                onClick();
              }
            }}
          >
            {btnText}
          </ModalButton>
        )}

        {showSecondBtn && (
          <ModalButton customClass="secondBtn" onClick={onClickSecondBtn}>
            {btnText2}
          </ModalButton>
        )}
      </div>
    </div>
  );
};

export default Modal;
